import { createRouter, createWebHistory } from 'vue-router';
import AppLayout from '@/layouts/AppLayout.vue';
import AuthLayout from '@/layouts/AuthLayout.vue';
import PublicLayout from '@/layouts/PublicLayout.vue';
import ResidentLayout from '@/layouts/ResidentLayout.vue';
import LoginView from '@/views/LoginView.vue';
import DashboardView from '@/views/DashboardView.vue';
import ReportsView from '@/views/ReportsView.vue';
import IssuesListView from '@/views/IssuesListView.vue';
import IssueDetailView from '@/views/IssueDetailView.vue';
import BoardMembersView from '@/views/BoardMembersView.vue';
import ResidentApprovalsView from '@/views/ResidentApprovalsView.vue';
import ResidentLoginView from '@/views/ResidentLoginView.vue';
import ResidentRegisterView from '@/views/ResidentRegisterView.vue';
import ResidentDashboardView from '@/views/ResidentDashboardView.vue';
import StubView from '@/views/StubView.vue';
import SettingsView from '@/views/SettingsView.vue';
const routes = [
    // Root — redirects in the guard below based on auth state.
    { path: '/', redirect: '/dashboard' },
    // MSAL post-sign-in landing. msalConfig.redirectUri =
    // http://localhost:5173/auth/callback. By the time the SPA navigates here,
    // initializeMsal() has already drained the response from the URL hash and
    // the auth store is hydrated — we just need to bounce the user to the
    // dashboard (or their pre-login intent, once we wire that up via state).
    { path: '/auth/callback', redirect: '/dashboard' },
    // Auth (no chrome)
    {
        path: '/',
        component: AuthLayout,
        children: [
            { path: 'login', name: 'login', component: LoginView, meta: { title: 'Sign in' } },
            {
                path: 'register',
                name: 'register',
                component: StubView,
                meta: { title: 'Register', slice: 'auth slice plan' },
            },
            {
                path: 'logout',
                name: 'logout',
                component: StubView,
                meta: { title: 'Signed out', slice: 'logout slice plan' },
            },
            {
                path: 'resident/login',
                name: 'resident-login',
                component: ResidentLoginView,
                meta: { title: 'Resident sign in' },
            },
            {
                path: 'resident/register',
                name: 'resident-register',
                component: ResidentRegisterView,
                meta: { title: 'Resident register' },
            },
            {
                // MSAL post-sign-in redirect for resident CIAM flow.
                path: 'resident/auth-callback',
                name: 'resident-auth-callback',
                redirect: '/resident',
            },
        ],
    },
    // ── Signup / onboarding flows ────────────────────────────────────────────────
    // These routes are intentionally outside the AuthLayout so they can render
    // their own chrome (matching the W7.7 prototype screens).
    {
        path: '/signup',
        name: 'signup-chooser',
        component: () => import('@/views/SignupChooserView.vue'),
        // Public — unauthenticated visitors can land here to pick their path.
    },
    {
        path: '/signup/board',
        name: 'signup-board',
        // Thin redirector: bounces already-onboarded board members away, triggers
        // login for unauthenticated users, and forwards true-onboarding users to
        // the HOA wizard. The component is never actually rendered.
        component: () => import('@/views/SignupChooserView.vue'),
        beforeEnter: async (_to, _from, next) => {
            const { useAuth } = await import('@/stores/auth');
            const auth = useAuth();
            if (!auth.ready)
                await auth.hydrate();
            if (!auth.isAuthenticated) {
                const { loginRedirect } = await import('@/auth/msalConfig');
                await loginRedirect();
                return;
            }
            if (auth.isBoard) {
                next('/');
                return;
            }
            next('/signup/board/create-hoa');
        },
    },
    {
        path: '/signup/board/create-hoa',
        name: 'signup-board-create-hoa',
        component: () => import('@/views/BoardCreateHoaView.vue'),
        meta: { requiresAuth: true },
    },
    {
        // NOTE: ResidentRegisterView reads its HOA slug from route.query.hoa (not
        // route.params.slug). The slug param here is therefore ignored by the view.
        // The view needs to be updated (in a future task) to also accept
        // route.params.slug so that deep-links like /signup/resident/cypress-ridge
        // pre-populate the HOA selection. For now the route is registered so CIAM
        // redirect URIs resolve; the view falls back to its default HOA slug.
        path: '/signup/resident/:slug',
        name: 'signup-resident',
        component: () => import('@/views/ResidentRegisterView.vue'),
        meta: { requiresAuth: true },
    },
    {
        // Public — the view self-manages auth (stashes the invite token, redirects
        // to CIAM login when the user is not yet authenticated).
        path: '/accept-invite',
        name: 'accept-invite',
        component: () => import('@/views/AcceptInviteView.vue'),
    },
    // Public portal (no auth, PublicLayout chrome — no sidebar, no MSAL)
    {
        path: '/portal',
        component: PublicLayout,
        children: [
            {
                path: ':slug',
                name: 'portal',
                component: () => import('@/views/portal/PortalSubmissionView.vue'),
                meta: { title: 'Submit an issue', layout: 'public' },
            },
        ],
    },
    // Authenticated (board + residents) — shared routes that don't require a
    // specific role. These use AppLayout chrome but are accessible to both
    // board members and approved residents.
    {
        path: '/',
        component: AppLayout,
        children: [
            {
                path: 'settings/notifications',
                name: 'notification-prefs',
                component: () => import('@/views/NotificationPrefsView.vue'),
                meta: { title: 'Notification preferences' },
            },
        ],
    },
    // Board (authenticated, board-only)
    {
        path: '/',
        component: AppLayout,
        meta: { requiresBoard: true },
        children: [
            {
                path: 'dashboard',
                name: 'dashboard',
                component: DashboardView,
                meta: { title: 'Dashboard', requiresBoard: true },
            },
            {
                path: 'issues',
                name: 'issues',
                component: IssuesListView,
                meta: { title: 'Issues', requiresBoard: true },
            },
            {
                path: 'issues/:id',
                name: 'issue-detail',
                component: IssueDetailView,
                meta: { title: 'Issue detail', requiresBoard: true },
            },
            {
                path: 'sources',
                name: 'sources',
                component: () => import('@/views/SourcesView.vue'),
                meta: { title: 'Sources', requiresBoard: true },
            },
            {
                path: 'sources/facebook',
                name: 'sources-facebook',
                component: () => import('@/views/FacebookSetupView.vue'),
                meta: { title: 'Facebook setup', requiresBoard: true },
            },
            {
                path: 'alert-rules',
                name: 'alert-rules',
                component: () => import('@/views/AlertRulesView.vue'),
                meta: {
                    title: 'Alert rules',
                    requiresBoard: true,
                },
            },
            {
                path: 'board-members',
                name: 'board-members',
                component: BoardMembersView,
                meta: { title: 'Board members', requiresBoard: true },
            },
            {
                path: 'residents/approvals',
                name: 'resident-approvals',
                component: ResidentApprovalsView,
                meta: { title: 'Resident approvals', requiresBoard: true },
            },
            {
                path: 'reports',
                name: 'reports',
                component: ReportsView,
                meta: { title: 'Reports', requiresBoard: true },
            },
            {
                path: 'settings/:section?',
                name: 'settings',
                component: SettingsView,
                meta: { title: 'Settings', requiresBoard: true },
            },
        ],
    },
    // Resident (authenticated, resident-only) — wrapped in ResidentLayout
    {
        path: '/resident',
        component: ResidentLayout,
        meta: { requiresResident: true },
        children: [
            {
                path: '',
                name: 'resident-dashboard',
                component: ResidentDashboardView,
                meta: { title: 'My HOA', requiresResident: true },
            },
        ],
    },
    // Error page (router.onError navigates here)
    {
        path: '/error',
        name: 'error',
        component: () => import('@/views/ErrorView.vue'),
    },
    // 404 — must be last
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFoundView.vue'),
    },
];
export const router = createRouter({
    history: createWebHistory(),
    routes,
});
router.onError((err, to) => {
    void router.push({
        name: 'error',
        query: {
            message: String(err.message ?? err),
            from: to.fullPath,
        },
    });
});
router.beforeEach(async (to, _from, next) => {
    // Dynamic import avoids circular-import issues (auth store imports msalConfig
    // which is also imported at module level in some paths).
    const { useAuth } = await import('@/stores/auth');
    const auth = useAuth();
    if (!auth.ready)
        await auth.hydrate();
    // ── Routes that are public or self-manage auth ───────────────────────────
    // Names must match the actual `name:` values defined above.
    const publicNames = new Set([
        'signup-chooser', // /signup
        'signup-board', // /signup/board — beforeEnter handles its own auth
        'accept-invite', // /accept-invite — view handles CIAM redirect
        'login', // /login
        'register', // /register (stub)
        'logout', // /logout (stub)
        'resident-login', // /resident/login
        'resident-register', // /resident/register
        'resident-auth-callback',
        'portal', // /portal/:slug
        'error',
        'not-found',
    ]);
    if (to.meta?.public || publicNames.has(to.name)) {
        return next();
    }
    // ── Resume a stashed invite-accept after CIAM redirect ───────────────────
    // AcceptInviteView stashes `pendingInviteToken` in sessionStorage before
    // calling loginRedirect (the user clicks an invite link while unauthenticated).
    // After CIAM round-trips them back here authenticated, route them back to
    // /accept-invite with the token so the POST fires. AcceptInviteView clears
    // sessionStorage on success; a failure leaves it for one more retry.
    if (auth.isAuthenticated && to.name !== 'accept-invite') {
        const pendingInviteToken = sessionStorage.getItem('pendingInviteToken');
        if (pendingInviteToken) {
            return next({ name: 'accept-invite', query: { token: pendingInviteToken } });
        }
    }
    // ── Not authenticated → trigger MSAL login ───────────────────────────────
    if (!auth.isAuthenticated) {
        sessionStorage.setItem('postLoginRedirect', to.fullPath);
        const { loginRedirect } = await import('@/auth/msalConfig');
        await loginRedirect();
        // loginRedirect() performs a full-page redirect; next() is never reached,
        // but returning keeps TypeScript happy.
        return;
    }
    // ── Authenticated but no role → onboarding state ─────────────────────────
    // Redirect to /signup unless the user is already in the signup flow.
    if (auth.isOnboarding && !to.path.startsWith('/signup')) {
        return next('/signup');
    }
    // ── Pending resident bouncing to the pending screen ───────────────────────
    // Keep the pre-existing behaviour: pending users can only see resident-dashboard.
    const requiresResident = to.matched.some((r) => r.meta.requiresResident);
    if (requiresResident && auth.isPending && to.name !== 'resident-dashboard') {
        return next('/resident');
    }
    // ── Role-mismatch redirects ───────────────────────────────────────────────
    const requiresBoard = to.matched.some((r) => r.meta.requiresBoard);
    if (requiresBoard && !auth.isBoard) {
        return next('/resident');
    }
    if (requiresResident && !auth.isResident) {
        return next('/');
    }
    // ── Authed users landing on the login screens → forward to their home ─────
    if (to.name === 'login' && auth.isBoard)
        return next('/dashboard');
    if (to.name === 'login' && auth.isResident)
        return next('/resident');
    if (to.name === 'resident-login' && auth.isResident)
        return next('/resident');
    return next();
});
